import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Users, Milk, Route, FolderTree, Globe2 } from "lucide-react";
import { toast } from "sonner";

/**
 * ZuordnungPicker – wiederverwendbare Auswahl für Zielgruppen.
 * value: { zuordnung_typ: 'alle'|'milchsorte'|'tour'|'gruppe', zuordnung_ids: string[] }
 */
export const ZuordnungPicker = ({ value, onChange, testIdPrefix = "zuord" }) => {
  const [milchsorten, setMilchsorten] = useState([]);
  const [touren, setTouren] = useState([]);
  const [gruppen, setGruppen] = useState([]);

  const typ = value?.zuordnung_typ || "alle";
  const ids = value?.zuordnung_ids || [];

  useEffect(() => {
    (async () => {
      try {
        const [m, t, g] = await Promise.all([
          api.get("/milchsorten").then((r) => r.data).catch(() => []),
          api.get("/touren").then((r) => r.data).catch(() => []),
          api.get("/gruppen").then((r) => r.data).catch(() => []),
        ]);
        setMilchsorten(Array.isArray(m) ? m : []);
        setTouren(Array.isArray(t) ? t : []);
        setGruppen(Array.isArray(g) ? g : []);
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, []);

  const setTyp = (v) => onChange({ zuordnung_typ: v, zuordnung_ids: [] });
  const toggleId = (id) => {
    const has = ids.includes(id);
    onChange({
      zuordnung_typ: typ,
      zuordnung_ids: has ? ids.filter((x) => x !== id) : [...ids, id],
    });
  };

  const optionen =
    typ === "milchsorte" ? milchsorten.map((x) => ({ id: x.id, label: x.name }))
    : typ === "tour"       ? touren.map((x) => ({ id: x.id, label: x.name }))
    : typ === "gruppe"     ? gruppen.map((x) => ({ id: x.id, label: x.name }))
    : [];

  return (
    <div className="space-y-2">
      <Label className="text-sm">Zielgruppe</Label>
      <Select value={typ} onValueChange={setTyp}>
        <SelectTrigger data-testid={`${testIdPrefix}-typ`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="alle">Alle Lieferanten</SelectItem>
          <SelectItem value="milchsorte">Nur bestimmte Milchsorten</SelectItem>
          <SelectItem value="tour">Nur bestimmte Touren</SelectItem>
          <SelectItem value="gruppe">Nur bestimmte Gruppen</SelectItem>
        </SelectContent>
      </Select>

      {typ === "alle" ? (
        <div className="flex items-center gap-2 rounded-md border bg-secondary/40 p-2 text-xs text-muted-foreground">
          <Globe2 className="h-3.5 w-3.5" /> Sichtbar für alle aktiven Lieferanten.
        </div>
      ) : (
        <div className="rounded-md border p-2 max-h-40 overflow-y-auto">
          {optionen.length === 0 ? (
            <p className="text-xs text-muted-foreground">Keine Einträge vorhanden.</p>
          ) : optionen.map((o) => (
            <label key={o.id}
              data-testid={`${testIdPrefix}-opt-${o.id}`}
              className="flex items-center gap-2 py-1 text-xs cursor-pointer hover:bg-secondary/40 rounded px-1">
              <Checkbox checked={ids.includes(o.id)}
                onCheckedChange={() => toggleId(o.id)} />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}
      {typ !== "alle" && (
        <div className="flex flex-wrap gap-1">
          {ids.map((id) => {
            const o = optionen.find((x) => x.id === id);
            return (
              <Badge key={id} variant="secondary" className="text-[10px]">
                {o?.label || id}
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const ZuordnungLabel = ({ typ, ids }) => {
  const Icon = typ === "milchsorte" ? Milk
            : typ === "tour"        ? Route
            : typ === "gruppe"      ? FolderTree
            : Users;
  const label = typ === "alle"
    ? "Alle Lieferanten"
    : `${(ids || []).length} ${typ === "milchsorte" ? "Milchsorte(n)"
                             : typ === "tour" ? "Tour(en)"
                             : "Gruppe(n)"}`;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
};

// Utility: prüft ob content noch als "Neu" gilt (X Tage seit erstellt_am)
export const istNeu = (erstellt_am, tage = 7) => {
  if (!erstellt_am || !tage) return false;
  const d = new Date(erstellt_am);
  const diff = (Date.now() - d.getTime()) / 86400000;
  return diff >= 0 && diff <= tage;
};
